import { SingleCheckOptionItem, SingleOptionField } from '@connected/form-fields/components/option';

import type { Option, OptionValue } from '../../types';
import type { FormTableFieldApi } from '../types';

export type SelectFieldProps<TValue extends OptionValue> = {
    /** Field API from FormTable cell renderer */
    field: FormTableFieldApi<TValue>;
    /** Options to display in the select */
    options: Option<TValue>[];
    /** Whether the field is disabled */
    disabled?: boolean;
    /** Additional class name */
    className?: string;
    /** Maximum items before scrolling */
    maxItemsBeforeScroll?: number;
    /** Called when field value changes (for custom side effects like API patching) */
    onValueChange?: (value: TValue) => void;
};

/**
 * SelectField - A dropdown select wrapper for use in FormTable cells.
 *
 * Integrates with FormTable's field API to provide form-connected select input.
 */
export function SelectField<TValue extends OptionValue>({
    field,
    options,
    disabled = false,
    className = 'w-full',
    maxItemsBeforeScroll = 8,
    onValueChange,
}: SelectFieldProps<TValue>) {
    return (
        <SingleOptionField
            name={field.name}
            state={{ value: field.state.value as TValue }}
            handleChange={(value) => {
                field.handleChange(value);
                // Trigger side effect (e.g., API patch) immediately on change
                if (onValueChange) {
                    onValueChange(value);
                }
            }}
            options={options}
            render={SingleCheckOptionItem}
            dropdown
            closeOnClick
            maxItemsBeforeScroll={maxItemsBeforeScroll}
            disabled={disabled || options.length === 0}
            className={className}
        />
    );
}
